import { useLocation } from 'react-router-dom'
import { useHeaderTabs } from '@e3dc-react/shell/Libs/Header'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useUserRoles } from 'src/Hooks/user'
import { useEffect, useMemo } from 'react'
import { TabsBarTab } from '@e3dc-react/shell/Elements/Tabs/TabsBar'

interface UseInstallationConfigurationTabs {
  tabs: TabsBarTab[]
  setTabs: (value: TabsBarTab[]) => void
  setActiveTab: (value: number) => void
}

/**
 * Tabs for the installation configuration page, which includes general settings, emc, storage and power meter.
 */
export const useInstallationConfigurationTabs = (installationId: number | undefined): UseInstallationConfigurationTabs => {
  const location = useLocation()
  const { setTabs, setActiveTab } = useHeaderTabs()
  const { t, selectedAppLanguage } = useCustomTranslation()
  const { isServiceUser } = useUserRoles()

  /*
   * Wallbox tab is temporarily disabled.
   *
   * Reason: The qntm backend currently does not support pre-configuring wallbox type
   * for controlled sub-devices. The device reports its type itself after coming
   * online, and the sub-device model lacks the required properties and wallbox
   * variant distinction.
   *
   * This is kept here (instead of removed) so we can re-enable it once wallbox
   * pre-configuration gets prioritized again.
   * See CYDO-3047 more details.
   */
  const tabs = useMemo(
    () => [
      {
        label: t('installation-configuration.tabs.general'),
        href: `/installation/${installationId}/configuration/general`,
        componentHref: '/general',
      },
      {
        label: t('installation-configuration.tabs.emc'),
        href: `/installation/${installationId}/configuration/emc`,
        componentHref: '/emc',
      },
      {
        label: t('installation-configuration.tabs.storage'),
        href: `/installation/${installationId}/configuration/storage`,
        componentHref: '/storage',
      },
      {
        label: t('installation-configuration.tabs.power-meter'),
        href: `/installation/${installationId}/configuration/power-meter`,
        componentHref: '/power-meter',
      },
      // {
      //   label: t('installation-configuration.tabs.wallbox'),
      //   href: `/installation/${installationId}/configuration/wallbox`,
      //   componentHref: '/wallbox',
      // },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedAppLanguage, installationId]
  )

  useEffect(() => {
    setTabs(tabs)

    return () => setTabs([])
  }, [tabs, setTabs, location.pathname, isServiceUser])

  return { tabs, setTabs, setActiveTab }
}
